import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote, CheckCircle2, AlertTriangle } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export const Journey = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      ref={ref}
      id="journey"
      className="relative py-28 sm:py-36 bg-[color:var(--mati-sand)]"
      data-testid="journey-section"
    >
      <div className="max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end mb-16">
          <motion.div {...reveal} className="col-span-12 lg:col-span-5">
            <div className="eyebrow text-[color:var(--mati-clay)]">
              01 · The Civic Context
            </div>
            <h2 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-[color:var(--mati-forest)] mt-5 leading-[0.95] tracking-tight">
              A city rising,{" "}
              <span className="italic text-[color:var(--mati-clay)]">
                one street
              </span>{" "}
              at a time.
            </h2>
          </motion.div>
          <motion.p
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            className="col-span-12 lg:col-span-5 lg:col-start-8 font-sub text-base sm:text-lg text-[color:var(--mati-muted)] leading-relaxed"
          >
            Morbi is not just a hub of ceramic tiles — it is our home. A clean
            city means better health, more tourism, higher property values, and
            pride for every citizen.
          </motion.p>
        </div>

        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          {/* Rank journey card */}
          <motion.div
            {...reveal}
            className="col-span-12 lg:col-span-7 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] rounded-3xl p-8 sm:p-12 relative overflow-hidden ceramic-grid-dark"
            data-testid="journey-rank-card"
          >
            <div className="absolute inset-0 opacity-30 ceramic-grid-dark pointer-events-none" />
            <div className="relative">
              <div className="eyebrow text-[color:var(--mati-clay)]">
                Rank Journey · Swachh Survekshan
              </div>
              <div className="mt-8 flex items-center gap-6 sm:gap-10">
                <div>
                  <div className="font-mono-ui text-xs tracking-widest opacity-60">
                    THEN
                  </div>
                  <div className="font-display text-6xl sm:text-7xl font-extrabold mt-2 line-through opacity-50">
                    234
                  </div>
                </div>
                <div className="flex-1 relative h-[2px] bg-[color:var(--mati-border-dark)]">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: "easeOut", delay: 0.3 }}
                    className="absolute inset-y-0 left-0 bg-[color:var(--mati-clay)]"
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.5 }}
                    className="absolute -top-6 left-1/2 -translate-x-1/2 font-mono-ui text-[10px] tracking-widest text-[color:var(--mati-clay)]"
                  >
                    +149 ranks
                  </motion.div>
                </div>
                <div>
                  <div className="font-mono-ui text-xs tracking-widest text-[color:var(--mati-clay)]">
                    NOW
                  </div>
                  <div className="font-display text-6xl sm:text-7xl font-extrabold mt-2 text-[color:var(--mati-clay)]">
                    85
                  </div>
                </div>
              </div>
              <p className="mt-10 font-sub text-base sm:text-lg opacity-90 max-w-xl leading-relaxed">
                Morbi Municipal Corporation (MMC) has been actively running
                cleanliness drives, plastic collection, and source-segregation
                enforcement under the Swachh Bharat Mission — climbing from
                234th to 85th nationally, and 7th in Gujarat.
              </p>
            </div>
          </motion.div>

          {/* Quote card with image */}
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.15 }}
            className="col-span-12 lg:col-span-5 relative rounded-3xl overflow-hidden min-h-[380px]"
            data-testid="journey-quote-card"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://images.unsplash.com/photo-1727689484465-c704c115740e?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Terracotta wall of Morbi"
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--mati-forest)] via-[color:var(--mati-forest)]/50 to-transparent" />
            <div className="relative p-8 sm:p-10 h-full flex flex-col justify-end text-[color:var(--mati-sand)]">
              <Quote className="w-8 h-8 text-[color:var(--mati-clay)] mb-4" />
              <blockquote className="font-display text-2xl sm:text-3xl leading-tight font-semibold">
                Cleanliness is a shared responsibility —{" "}
                <span className="italic text-[color:var(--mati-clay)]">
                  not just a municipal job.
                </span>
              </blockquote>
              <div className="mt-5 font-mono-ui text-[10px] uppercase tracking-[0.28em] opacity-70">
                Morbi generates ~248 tonnes of waste every day
              </div>
            </div>
          </motion.div>

          {/* Working */}
          <motion.div
            {...reveal}
            className="col-span-12 md:col-span-6 bg-white border border-[color:var(--mati-border)] rounded-3xl p-8 relative"
            data-testid="journey-working-card"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-full bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div className="eyebrow text-[color:var(--mati-forest)]">
                What's already working
              </div>
            </div>
            <ul className="space-y-3 font-sub text-[color:var(--mati-ink)]">
              {[
                "Weekly 'Swachhata Mitra' volunteer clean-ups every Sunday",
                "Ward-wise cleanliness groups being formed",
                "Mandatory wet / dry dustbins for commercial & religious places",
                "Plastic waste collection drives across market areas",
                "'One Day One Hour' mass events with 45,000+ citizens",
              ].map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 pb-3 border-b border-[color:var(--mati-border)] last:border-0 last:pb-0"
                >
                  <span className="font-mono-ui text-[10px] text-[color:var(--mati-clay)] mt-1.5">
                    0{i + 1}
                  </span>
                  <span className="text-[15px] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Needs attention */}
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            className="col-span-12 md:col-span-6 bg-[color:var(--mati-sand-2)] rounded-3xl p-8 relative border border-[color:var(--mati-border)]"
            data-testid="journey-attention-card"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-full bg-[color:var(--mati-clay)] text-[color:var(--mati-sand)] flex items-center justify-center">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="eyebrow text-[color:var(--mati-clay)]">
                What still needs attention
              </div>
            </div>
            <ul className="space-y-3 font-sub text-[color:var(--mati-ink)]">
              {[
                "Open dumping at garbage-vulnerable points",
                "Mixed waste reaching landfills without segregation",
                "Single-use plastic litter on roadsides and drains",
                "Lack of community bins in several wards",
              ].map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 pb-3 border-b border-[color:var(--mati-border)] last:border-0 last:pb-0"
                >
                  <span className="font-mono-ui text-[10px] text-[color:var(--mati-forest)] mt-1.5">
                    0{i + 1}
                  </span>
                  <span className="text-[15px] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Journey;
